import React, {useCallback, useMemo, useState} from 'react';
import {Concept} from 'types';
import type {
  ColumnSet,
  ColumnWithLabel,
  ViewMode,
} from 'services/advancedSearch/types';
import {useLocalization} from 'localization';
import {Dialog, FieldLabel, Layout, Select, Spacer} from 'components';
import {ColumnPicker} from '../components/ColumnPicker';

export const useDownloadDialog = (
  allColumnSets: ColumnSet[],
  allColumns: ColumnWithLabel[],
  currentColumns: string[],
  onDownload: (mode: ViewMode, columns?: string[]) => Promise<void>,
) => {
  const {t} = useLocalization();
  const [open, setOpen] = useState<boolean>(false);

  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(currentColumns);
  const [viewMode, setViewMode] = useState<ViewMode>(Concept.manifestation);
  const [downloading, setDownloading] = useState<boolean>(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    setDownloading(true);

    onDownload(viewMode, selectedColumns).then(() => {
      setDownloading(false);
      handleClose();
    });
  }, [onDownload, viewMode, selectedColumns, handleClose]);

  const Form = useMemo(() => {
    return (
      <Dialog
        isOpen={open}
        fullWidth
        maxWidth={'md'}
        title={t('page.search.filter.download.title')}
        body={
          <Layout flex={1}>
            <FieldLabel
              label={t('page.search.filter.viewmode.label')}
              required
            />
            <Select<ViewMode>
              value={viewMode}
              options={[
                {
                  value: Concept.manifestation,
                  label: t(`general.entity.${Concept.manifestation}`),
                },
                {
                  value: Concept.work,
                  label: t(`general.entity.${Concept.work}`),
                },
              ]}
              onChange={value => setViewMode(value)}
              required
            />
            <Spacer height={2} />
            <ColumnPicker
              viewMode={viewMode}
              initialColumns={selectedColumns}
              allColumnSets={allColumnSets}
              allColumns={allColumns}
              onChange={setSelectedColumns}
            />
          </Layout>
        }
        dialogItems={[
          {
            itemType: 'action',
            showSpinnerOnClick: true,
            disabled: downloading,
            title: t('general.ok'),
            onClick: () => handleSave(),
          },
          {
            itemType: 'action',
            type: 'reset',
            alignLeft: true,
            disabled: downloading,
            title: t('general.cancel'),
            onClick: () => handleClose(),
          },
        ]}
      />
    );
  }, [
    open,
    t,
    viewMode,
    selectedColumns,
    allColumnSets,
    allColumns,
    downloading,
    handleSave,
    handleClose,
  ]);

  return {
    DownloadCsvDialog: Form,
    openDownloadCsvDialog: handleOpen,
  };
};
